import { actions } from "./slice";
import { db } from "initializer";
import sendEvent from "modules/sendEvent";
import { createSubscribeCollection } from "../firestoreModuleUtils/operators";
import { uploadFile, deleteFile } from "modules/firebase-utils/uploader";
import { getMediumById, getUserMediumIds } from "./selectors";
import { UserMediumRecord } from "./records";
import { UpdateUserMedium, UserMedium } from "./";
import { DefaultThunk } from "stores";
import { getUid } from "../app.user/selectors";
import {
  CollectionReference,
  DocumentData,
  FirestoreDataConverter,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  calcOrderAppendingToTail,
  calcOrderByDrag,
  forceAlignOrders,
  moveIdByIndex,
} from "../firestoreModuleUtils/ordaring";
import { DragEndEvent } from "@dnd-kit/core";
import i18next from "i18next";
import { appStateMutate } from "../app.state/operations";

const mediumConverter: FirestoreDataConverter<DocumentData> = {
  toFirestore(character: UpdateUserMedium) {
    return character;
  },
  fromFirestore(snapshot, options): UserMedium {
    const data = snapshot.data(options)!;
    return UserMediumRecord(data);
  },
};

const mediumRef = (uid: string) =>
  collection(db, "users", uid, "media").withConverter(
    mediumConverter
  ) as CollectionReference<UserMedium>;

export const subscribeUserMedia = createSubscribeCollection<
  UserMedium,
  { uid: string; dirs: string[] }
>(actions, ({ uid, dirs }) =>
  query(mediumRef(uid), where("dir", "in", dirs), orderBy("order"))
);

const LIMIT_USER_MEDIUM_SIZE = 10 * 1024 * 1024; // 10 MiB

export const addUserMediumFiles =
  (uid: string, roomId: string, dir: string, files: File[]): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const ids = getUserMediumIds(state, dir);
    let order = calcOrderAppendingToTail(
      ids,
      state.entities.userMedia.entities
    );

    for (const file of files) {
      sendEvent("addUserMedium");
      if (file.size >= LIMIT_USER_MEDIUM_SIZE) {
        window.alert(
          i18next.t(
            "ファイルサイズが10MBを超えているため、アップロードに失敗しました。"
          )
        );
        continue;
      }

      uploadFile(file, {
        collectionRef: mediumRef(uid),
        data: UserMediumRecord({
          owner: uid,
          roomId,
          dir,
          volume: 0.5,
          loop: true,
          order: order,
          external: false,
        }),
      });

      order++;
    }
  };

export const addUserMediumFromUrl =
  (uid: string, roomId: string, dir: string, url: string): DefaultThunk =>
  (_, getState) => {
    sendEvent("addUserMedium");
    const state = getState();
    const ids = getUserMediumIds(state, dir);
    return addDoc(
      mediumRef(uid),
      UserMediumRecord({
        owner: uid,
        roomId,
        dir,
        volume: 0.5,
        loop: true,
        order: calcOrderAppendingToTail(ids, state.entities.userMedia.entities),
        url,
        name: "新規メディア",
        uploaded: false,
        archived: false,
        contentType: null,
        size: 0,
        updatedAt: Date.now(),
        createdAt: Date.now(),
        external: true,
      })
    );
  };

export const updateUserMedium =
  (uid: string, mediumId: string, values: UpdateUserMedium) => () => {
    updateDoc(doc(mediumRef(uid), mediumId), {
      ...values,
    });
  };

export const deleteUserMedium =
  (uid: string, mediumId: string, external?: boolean) =>
  async (dispatch, _) => {
    sendEvent("deleteUserMedium");
    if (external) {
      await deleteDoc(doc(mediumRef(uid), mediumId));
    } else {
      await deleteFile(doc(mediumRef(uid), mediumId));
    }
    dispatch(
      appStateMutate((state) => {
        const index = state.selectedMediaIds.indexOf(mediumId);
        if (index >= 0) {
          state.selectedMediaIds.splice(index, 1);
        }
      })
    );
  };

export const deleteUserMediumByDirectory =
  (uid: string, directoryId: string): DefaultThunk<Promise<void>> =>
  async (_, getState) => {
    const state = getState();
    const ids = getUserMediumIds(state, directoryId);
    if (ids.length === 0) {
      return;
    }

    const promises: Promise<void>[] = [];
    const batch = writeBatch(db);
    for (const id of ids) {
      const medium = getMediumById(state, id);
      if (medium.external) {
        batch.delete(doc(mediumRef(uid), id));
      } else {
        promises.push(deleteFile(doc(mediumRef(uid), id)));
      }
    }
    await Promise.all([...promises, batch.commit()]);
  };

export const reorderMedia =
  (_uid: string, dir: string, order: DragEndEvent): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const uid = getUid(state);
    const srcIndex = order.active.data.current?.sortable.index;
    const destIndex = order.over?.data.current?.sortable.index;

    if (!uid || destIndex === srcIndex) {
      return;
    }

    const ids = getUserMediumIds(state, dir);
    const targetId = ids[srcIndex];
    if (!targetId || ids.length <= destIndex || ids.length <= srcIndex) {
      return;
    }

    dispatch(
      actions.groupReorder({
        group: dir,
        startIndex: srcIndex,
        endIndex: destIndex,
      })
    );

    const newOrder = calcOrderByDrag(ids, state.entities.userMedia.entities, {
      destIndex,
      srcIndex,
    });

    if (newOrder === null) {
      const movedIds = moveIdByIndex(ids, { destIndex, srcIndex });
      forceAlignOrders(movedIds, uid, mediumRef);
    } else {
      updateDoc(doc(mediumRef(uid), targetId), { order: newOrder });
    }
  };

export const changeDirMedia =
  (mediumId: string, dir: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const uid = getUid(state);
    const mediumIds = getUserMediumIds(state, dir);
    if (!uid || !mediumId) return;
    dispatch(actions.groupRemove(mediumId)); // remove once local state

    let order = 0;
    if (mediumIds.length > 0) {
      order = calcOrderAppendingToTail(
        mediumIds,
        state.entities.userMedia.entities
      );
    } else {
      const snapshot = await getDocs(
        query(mediumRef(uid), where("dir", "==", dir))
      );
      const maxOrder = snapshot.docs.reduce(
        (acc, doc) => Math.max(acc, doc.data().order || 0),
        0
      );
      order = maxOrder + 1;
    }

    updateDoc(doc(mediumRef(uid), mediumId), { order, dir });
  };

export const deleteUserSelectedMedia =
  (selectedMediaIds: string[]): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const uid = getUid(state);
    if (!uid) return;
    sendEvent("deleteUserSelectedMedia");
    const mediaList = selectedMediaIds.map((id) => {
      const medium = getMediumById(state, id);
      return { ...medium, _id: id };
    });
    const deleteMedia = mediaList.map((media) => {
      if (media.external) {
        return deleteDoc(doc(mediumRef(uid), media._id));
      }
      return deleteFile(doc(mediumRef(uid), media._id));
    });

    await Promise.all(deleteMedia);

    selectedMediaIds.forEach((id) => {
      dispatch(actions.remove(id));
    });
  };

export const changeDirSelectedMedia =
  (selectedMediaIds: string[], dir: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const uid = getUid(state);
    if (!uid) return;
    const mediumIds = getUserMediumIds(state, dir);
    const selectMedia = selectedMediaIds.map((id) => getMediumById(state, id));
    selectMedia.sort((a, b) => a.order - b.order);

    let order = 0;
    if (mediumIds.length > 0) {
      order = calcOrderAppendingToTail(
        mediumIds,
        state.entities.userMedia.entities
      );
    } else {
      const snapshot = await getDocs(
        query(mediumRef(uid), where("dir", "==", dir))
      );
      const maxOrder = snapshot.docs.reduce(
        (acc, doc) => Math.max(acc, doc.data().order || 0),
        0
      );
      order = maxOrder + 1;
    }

    const batch = writeBatch(db);
    selectMedia.forEach((medium) => {
      batch.update(doc(mediumRef(uid), medium._id), { order, dir });
      order += 1;
    });
    await batch.commit();

    selectedMediaIds.forEach((id) => {
      dispatch(actions.groupRemove(id));
    });
  };
